import { usePostPcrTestRunApi } from 'api/pcr_test_run'
import { Flex } from 'components/shared/flex'
import { FileField, InputField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Button, Modal, Table } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { PcrTestRun, PcrTestRunForm } from 'entities/pcr_test_run'
import { PcrTestSampleForm } from 'entities/pcr_test_sample'
import { useBulkPutPcrTestSamplesApi } from 'api/pcr_test_sample'
import { useFetchProductItemsApi } from 'api/product_item'

const FormView = (form: Form<PcrTestRunForm>, apiError: ApiError) => {
    return (
        <>
            <Flex>
                <FileField form={form} attr="rawData" label="Raw data" required />
            </Flex>
        </>
    )
}

type NewPcrTestRunFormProps = {
    onComplete?: () => void
}

export const NewPcrTestRunForm = (props: NewPcrTestRunFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostPcrTestRunApi()
    const form = useForm<PcrTestRunForm>({})
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>テストランの登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>{FormView(form, api.apiError)}</>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

type EditPcrTestRunFormProps = {
    pcrTestRun: PcrTestRun
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditPcrTestSamplesForm = (props: EditPcrTestRunFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = useBulkPutPcrTestSamplesApi()
    const form = useForm<PcrTestSampleForm[]>(props.pcrTestRun.pcrTestSamples || [])
    const productApi = useFetchProductItemsApi()

    useEffect(() => {
        productApi.execute()
    }, [])

    useEffectSkipFirst(() => {
        form.set(props.pcrTestRun.pcrTestSamples || [])
    }, [props.pcrTestRun])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>テスト検体の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Table>
                    <thead>
                        <tr>
                            <th>検体ID</th>
                            <th>年齢</th>
                            <th>性別</th>
                            <th>プロダクト</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.pcrTestRun.pcrTestSamples?.map((pcrTestSample, index) => {
                            return (
                                <tr key={pcrTestSample.code}>
                                    <td>{pcrTestSample.code}</td>
                                    <td>
                                        <InputField form={form} attr={[index, 'age']} apiError={api.apiError} type="number" />
                                    </td>
                                    <td>
                                        <SelectField
                                            form={form}
                                            attr={[index, 'gender']}
                                            apiError={api.apiError}
                                            selectItems={[
                                                { label: '男性', value: 'MALE' },
                                                { label: '女性', value: 'FEMALE' },
                                            ]}
                                            includeBlank
                                        />
                                    </td>
                                    <td>
                                        <SelectField
                                            form={form}
                                            attr={[index, 'productItemId']}
                                            apiError={api.apiError}
                                            selectItems={productApi.response.results.map((p) => {
                                                return { label: p.name, value: p.id! }
                                            })}
                                            includeBlank
                                        />
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
